import { html } from 'htm/preact';

/**
 * O Sol com a Lua por cima, na posição em que a **área tapada** é exactamente a
 * ocultação do concelho. Não é um desenho decorativo com um recorte a olho: o
 * afastamento entre os dois centros é resolvido a partir da fórmula da área de
 * intersecção de dois círculos.
 *
 * Simplificação assumida: raios aparentes iguais. No eclipse de 12 de agosto de 2026
 * a Lua é ~3% maior que o Sol; num glifo de 72 px isso é sub-pixel, e assumir raios
 * iguais evita ter de embarcar os raios aparentes por concelho só para desenhar isto.
 * A ocultação mostrada em número vem sempre do gerador, nunca deste desenho.
 */

/**
 * Área de intersecção de dois círculos de raio `r` com centros a `d`, como fracção
 * da área do disco. Decrescente em `d`, por isso é invertível por bissecção.
 */
function overlapFraction(d: number, r: number): number {
    if (d <= 0) return 1;
    if (d >= 2 * r) return 0;
    const area = 2 * r * r * Math.acos(d / (2 * r)) - (d / 2) * Math.sqrt(4 * r * r - d * d);
    return area / (Math.PI * r * r);
}

/** Bissecção — 40 iterações chegam a ~1e-12 no intervalo [0, 2r]. */
function separationFor(fraction: number, r: number): number {
    const target = Math.min(1, Math.max(0, fraction));
    let lo = 0;
    let hi = 2 * r;
    for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (overlapFraction(mid, r) > target) lo = mid;
        else hi = mid;
    }
    return (lo + hi) / 2;
}

export function EclipseDisc({ obscurationPct, size = 72 }: { obscurationPct: number; size?: number }) {
    const r = size * 0.34;
    const cx = size / 2;
    const cy = size / 2;
    const d = separationFor(obscurationPct / 100, r);
    // A Lua entra por cima e pela esquerda — a direcção real do movimento aparente
    // neste eclipse visto de Portugal, não uma escolha estética arbitrária.
    const angle = -140 * (Math.PI / 180);
    const mx = cx + d * Math.cos(angle);
    const my = cy + d * Math.sin(angle);

    return html`
        <svg class="eclipse-disc" width=${size} height=${size} viewBox=${`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
            <circle cx=${cx} cy=${cy} r=${r * 1.28} class="eclipse-disc__corona" />
            <circle cx=${cx} cy=${cy} r=${r} class="eclipse-disc__sun" />
            <circle cx=${mx} cy=${my} r=${r} class="eclipse-disc__moon" />
        </svg>
    `;
}
